import React, { useEffect, useRef, useState } from 'react'
import styles from './styles.module.css'
import { PALETTE } from '../../../../styles/constants'
import Text from '../../../Text'
import Cta from '../../../Cta'
import ChevronRightIcon from '../../../icons/ChevronRightIcon'
import { useDetectMobile } from '../../../../utils/hooks/useDetectMobile'
import { toSrc } from '../../../../utils/imageSrc'

const cx = (...c) => c.filter(Boolean).join(' ')

// Formerly exported from ./elements.js; only SLIDES.gap is consumed by the JS
// below (the rest were pixel constants now inlined into styles.module.css).
const SLIDES = { gap: 8 }

function Carousel({ backgroundColor, dataPacks, slideCtaText, carouselCta }) {
  const [isMobile] = useDetectMobile(767)

  // Phase B: the highlighted/centered slide index is LOCAL to this carousel
  // (was Overmind selectedId — read only here).
  const [selectedId, setSelectedId] = useState(0)

  const slides = useRef(null)
  const firstClone = useRef(null)
  const lastClone = useRef(null)
  const nextControl = useRef(null)
  const prevControl = useRef(null)

  const [slideWidth, setSlideWidth] = useState(null)

  useEffect(() => {
    // execute on component initial load/mount
    if (window !== undefined) {
      updateSlideWidth()
      window.addEventListener('resize', updateSlideWidth)
    }
  })

  useEffect(() => {
    // execute on component unmount
    return () => {
      window.removeEventListener('resize', updateSlideWidth)
    }
  }, [])

  useEffect(() => {
    // execute if any window resize occurs, this will "restart" the slide
    moveToSlide(2, slideWidth, false)
  }, [isMobile, slideWidth])

  const updateSlideWidth = () => {
    if (slides.current) {
      const slideWidth = slides.current.children[selectedId].offsetWidth + SLIDES.gap
      setSlideWidth(slideWidth)
    }
  }

  const disableControlsAfterClick = () => {
    prevControl.current.style.pointerEvents = 'none'
    nextControl.current.style.pointerEvents = 'none'
    setTimeout(() => prevControl.current.style.pointerEvents = 'all', 100)
    setTimeout(() => nextControl.current.style.pointerEvents = 'all', 100)
  }

  const hasReachedForwardsEnd = () => {
    return slides.current && slides.current.children[selectedId + 1].id === firstClone.current.id
  }

  const hasReachedBackwardsEnd = () => {
    return slides.current && slides.current.children[selectedId].id === lastClone.current.id
  }

  const handleNextClick = () => {
    disableControlsAfterClick()

    if (hasReachedForwardsEnd()) {
      moveToNextSlide()
      setTimeout(() => moveToNextSlide(), 1)
    } else {
      moveToNextSlide()
    }
  }


  const handlePrevClick = () => {
    disableControlsAfterClick()

    if (hasReachedBackwardsEnd()) {
      moveToPrevSlide()
      setTimeout(() => moveToPrevSlide(), 1)
    } else {
      moveToPrevSlide()
    }
  }

  const moveToNextSlide = () => {
    if (hasReachedForwardsEnd()) {
      moveToSlide(1, slideWidth, true)
    } else {
      moveToSlide(selectedId + 1, slideWidth, false)
    }
  }

  const moveToPrevSlide = () => {
    if (hasReachedBackwardsEnd()) {
      moveToSlide(slides.current.children.length - 2, slideWidth, true)
    } else {
      moveToSlide(selectedId - 1, slideWidth, false)
    }
  }

  const moveToSlide = (slideId, slideWidth, stopTransition) => {
    slides.current.style.transition = stopTransition ? 'none' : 'all 500ms ease-in-out'
    slides.current.style.transform = `translateX(${-slideWidth * (slideId - (isMobile ? 0 : 1))}px)`
    setSelectedId(slideId)
  }

  const renderStat = (count, caption) => {
    return (
      <div className={styles.statWrapper}>
        <Text type="h4">{count}</Text>
        <Text type="body-short-1">{caption}</Text>
      </div>
    )
  }

  const renderSlide = (d, i, ref, id) => {
    const isSelected = selectedId === i ||
      (hasReachedBackwardsEnd() && i === slides.current.children.length - 2) ||
      (hasReachedForwardsEnd() && i === 1)

    return (
      <div className={cx(styles.slide, isSelected && styles.slideSelected)} key={d.name} ref={ref} id={id}>
        <div className={cx(styles.chip, (isSelected && d.tag) && styles.chipShow)}><Text type="h5">{d.tag}</Text></div>
        <div
          className={cx(styles.imageWrapper, !isSelected && styles.imageWrapperCenter)}
          style={{ backgroundImage: `url(${toSrc(d.imageUrl)})` }}
        />
        <div className={cx(styles.contentWrapper, isSelected && styles.contentWrapperShow)}>
          <div className={styles.textWrapper}>
            <Text type="h5">{d.seriesName}</Text>
            <Text type="body-short-1">Part of: {d.catalogue.name}</Text>
          </div>
          <div className={styles.dataPackStatsWrapper}>
            {d.setupsCount > 0 && <div className={styles.separator}/>}
            {d.setupsCount > 0 && renderStat(d.setupsCount, 'Setups')}
            {d.tutorialsCount > 0 && <div className={styles.separator}/>}
            {d.tutorialsCount > 0 && renderStat(d.tutorialsCount, 'Tutorials')}
            {d.hotlapsCount > 0 && <div className={styles.separator}/>}
            {d.hotlapsCount > 0 && renderStat(d.hotlapsCount, 'Hotlaps')}
            {(d.setupsCount > 0 || d.tutorialsCount > 0 || d.hotlapsCount > 0) && <div className={styles.separator}/>}
          </div>
          <div className={styles.ctaWrapper}>
            <Cta primary dark fullHeight title={slideCtaText} link={d.link}/>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className={styles.wrapper} style={{ background: backgroundColor.value || PALETTE.gray30 }}>
        <div className={cx(styles.controls, styles.controlsShow)}>
          <div className={styles.controlWrapper} onClick={handlePrevClick} ref={prevControl} style={{ '--control-rotate': '180deg' }}><ChevronRightIcon/></div>
          <div className={styles.controlWrapper} onClick={handleNextClick} ref={nextControl}><ChevronRightIcon/></div>
        </div>
        <div className={styles.indicators}>
          { dataPacks.map((d, i) => {
            let isSelected = selectedId !== 1 ? selectedId - 2 === i : i === dataPacks.length - 1
            return <div className={cx(styles.indicator, isSelected && styles.indicatorSelected)} key={i}/>
          }) }
        </div>
        <div className={cx(styles.slides, styles.slidesShow)} ref={slides}>

          { renderSlide(dataPacks[dataPacks.length - 2], 0, null, 'second-last-clone') }
          { renderSlide(dataPacks[dataPacks.length - 1], 1, lastClone, 'last-clone') }

          { dataPacks.map((d, i) => renderSlide(d, i + 2)) }

          { renderSlide(dataPacks[0], dataPacks.length + 2, firstClone, 'first-clone') }
        </div>
        <div className={styles.carouselCtaWrapper}>
          <Cta {...carouselCta} fullHeight/>
        </div>
      </div>
    </>
  )
}

export default Carousel
